import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * V1.42.9 · Sección plegable para SettingsTab.
 * - `<details>` nativo por accesibilidad + zero-JS baseline.
 * - Persiste estado abierto/cerrado en localStorage (`collapsible:${id}`).
 * - Se abre automáticamente si el hub navega a su `id` mediante el evento
 *   `settings:open` (o si la URL termina en `#id`).
 * - Header con título + subtítulo + chevron animado.
 */
export default function CollapsibleSection({ id, title, subtitle, defaultOpen = false, children }) {
  const key = `collapsible:${id}`;
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return defaultOpen;
    const stored = localStorage.getItem(key);
    if (stored === "1") return true;
    if (stored === "0") return false;
    return defaultOpen;
  });
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(key, open ? "1" : "0");
  }, [open, key]);

  // Escucha el evento del hub para autoabrirse cuando se navega a su id.
  useEffect(() => {
    const onOpen = (ev) => {
      if (ev?.detail === id) {
        setOpen(true);
        // scroll suave hasta la cabecera del bloque
        setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
      }
    };
    window.addEventListener("settings:open", onOpen);
    // También abrir si la URL trae #id
    if (typeof window !== "undefined" && window.location.hash === `#${id}`) {
      setOpen(true);
    }
    return () => window.removeEventListener("settings:open", onOpen);
  }, [id]);

  // Escucha "expandir/colapsar todo" desde la barra superior de SettingsTab.
  useEffect(() => {
    const expandAll = () => setOpen(true);
    const collapseAll = () => setOpen(false);
    window.addEventListener("collapsibles:expand-all", expandAll);
    window.addEventListener("collapsibles:collapse-all", collapseAll);
    return () => {
      window.removeEventListener("collapsibles:expand-all", expandAll);
      window.removeEventListener("collapsibles:collapse-all", collapseAll);
    };
  }, []);

  return (
    <div id={id} ref={ref} data-testid={`collapsible-${id}`}>
      <details
        open={open}
        onToggle={(e) => setOpen(e.currentTarget.open)}
        className="group rounded-2xl border border-neutral-200 bg-white overflow-hidden transition-shadow open:shadow-sm"
      >
        <summary
          className="flex items-center gap-3 cursor-pointer select-none list-none px-4 py-3 hover:bg-neutral-50 [&::-webkit-details-marker]:hidden"
          data-testid={`collapsible-summary-${id}`}
        >
          <ChevronDown className="h-4 w-4 text-neutral-500 shrink-0 transition-transform group-open:rotate-0 -rotate-90" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-neutral-900 truncate">{title}</div>
            {subtitle && (
              <div className="text-[11px] text-neutral-500 truncate">{subtitle}</div>
            )}
          </div>
        </summary>
        <div className="px-4 pb-4 pt-1 border-t border-neutral-100">
          {children}
        </div>
      </details>
    </div>
  );
}
